import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePeriod } from '../lib/period';
import { api, fmtNum, fmtDate } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/StatusBadge';
import { WhatsAppCard } from '../components/WhatsAppCard';
import {
  FlaskConical, AlertTriangle, AlertCircle, CheckCircle2, HelpCircle,
  Table2, Database, BookOpen, ArrowRight, PackageCheck, FileText, Clock, Sparkles, Calendar, ArrowUpDown,
} from 'lucide-react';

const KPIS = [
  { key: 'total_reagen', label: 'Total Reagen', accent: 'bg-primary', Icon: FlaskConical, isTotal: true },
  { key: 'critical', label: 'Kritis', accent: 'bg-red-600', Icon: AlertTriangle },
  { key: 'warning', label: 'Waspada', accent: 'bg-amber-500', Icon: AlertCircle },
  { key: 'safe', label: 'Aman', accent: 'bg-emerald-600', Icon: CheckCircle2 },
  { key: 'unknown', label: 'Perlu Cek', accent: 'bg-slate-400', Icon: HelpCircle },
];

const QUICK = [
  { to: '/pemantauan', label: 'Pemantauan Stock', desc: 'Lihat tabel pemakaian harian & sisa stock', Icon: Table2 },
  { to: '/master-reagen', label: 'Master Reagen', desc: 'Kelola data reagen & buffer stock', Icon: FlaskConical },
  { to: '/data-lis', label: 'Data LIS', desc: 'Data LIS mentah & pemetaan test', Icon: Database },
  { to: '/prf', label: 'Purchase Request', desc: 'Daftar purchase request reagen', Icon: FileText },
  { to: '/penerimaan', label: 'Penerimaan', desc: 'Riwayat penerimaan barang', Icon: PackageCheck },
  { to: '/analisis-excel', label: 'Analisis Excel', desc: 'Ringkasan struktur & rumus Excel', Icon: BookOpen },
];

const STATUS_VIEWS = {
  critical: { label: 'Reagen Kritis', Icon: AlertTriangle, iconCls: 'text-red-600', type: 'stock' },
  warning: { label: 'Reagen Waspada', Icon: AlertCircle, iconCls: 'text-amber-500', type: 'stock' },
  expired: { label: 'Reagen Expired', Icon: AlertTriangle, iconCls: 'text-red-600', type: 'ed' },
  ed_under_3: { label: 'Reagen ED < 3 Bulan', Icon: Clock, iconCls: 'text-orange-500', type: 'ed' },
  ed_3_6: { label: 'Reagen ED 3 - 6 Bulan', Icon: Calendar, iconCls: 'text-yellow-500', type: 'ed' },
};

function PrfIndicator({ prf }) {
  const list = prf || [];
  if (list.length === 0) return null;
  const hasReceived = list.some((p) => p.status === 'received');
  const hasPending = list.some((p) => p.status !== 'received');
  return (
    <span className="inline-flex items-center gap-1">
      {hasReceived && (
        <Badge className="bg-emerald-600 text-white text-[10px]" data-testid="prf-indicator-diterima">
          <CheckCircle2 className="mr-1 h-3 w-3" /> PRF Diterima
        </Badge>
      )}
      {hasReceived && hasPending && (
        <Badge className="bg-blue-600 text-white text-[10px]" data-testid="prf-indicator-baru">
          <Sparkles className="mr-1 h-3 w-3" /> PRF Baru
        </Badge>
      )}
      {!hasReceived && hasPending && (
        <Badge className="bg-amber-500 text-slate-950 text-[10px]" data-testid="prf-indicator-pending">
          <Clock className="mr-1 h-3 w-3" /> PRF Pending
        </Badge>
      )}
    </span>
  );
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"];

export default function Dashboard() {
  const { year, month } = usePeriod();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('critical');
  const [sortBy, setSortBy] = useState('name_asc');

  useEffect(() => {
    setLoading(true);
    api.monitoring(year, month)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month]);

  const viewInfo = STATUS_VIEWS[view];

  const shown = (data?.rows || []).filter((r) => {
    if (viewInfo.type === 'stock') {
      return r.status === view;
    } else {
      return r.status_ed === view;
    }
  });

  // LOGIKA SORT BY DASHBOARD
  const sortedRows = [...shown].sort((a, b) => {
    if (sortBy === 'name_asc') {
      return (a.nama_reagen || '').localeCompare(b.nama_reagen || '');
    }
    if (sortBy === 'stock_asc') {
      const stockA = viewInfo.type === 'ed' ? (a.sisa_test_fefo ?? a.sisa_stock) : a.sisa_stock;
      const stockB = viewInfo.type === 'ed' ? (b.sisa_test_fefo ?? b.sisa_stock) : b.sisa_stock;
      return (stockA || 0) - (stockB || 0);
    }
    if (sortBy === 'stock_desc') {
      const stockA = viewInfo.type === 'ed' ? (a.sisa_test_fefo ?? a.sisa_stock) : a.sisa_stock;
      const stockB = viewInfo.type === 'ed' ? (b.sisa_test_fefo ?? b.sisa_stock) : b.sisa_stock;
      return (stockB || 0) - (stockA || 0);
    }
    if (sortBy === 'ed_asc') {
      const dateA = a.expired_date || '9999-12-31';
      const dateB = b.expired_date || '9999-12-31';
      return dateA.localeCompare(dateB);
    }
    return 0;
  });

  const currentMonthName = MONTH_NAMES[month - 1];
  const prevMonthIndex = month === 1 ? 11 : month - 2;
  const prevMonthName = MONTH_NAMES[prevMonthIndex];

  const getTabCount = (key) => {
    if (STATUS_VIEWS[key].type === 'stock') {
      return data?.counts?.[key] ?? 0;
    }
    return data?.counts_ed?.[key] ?? 0;
  };

  const getTabClass = (key, isActive) => {
    if (!isActive) return 'text-muted-foreground hover:bg-muted';
    switch (key) {
      case 'critical':
      case 'expired':
        return 'bg-red-600 text-white';
      case 'warning':
        return 'bg-amber-500 text-slate-950';
      case 'ed_under_3':
        return 'bg-orange-500 text-white';
      case 'ed_3_6':
        return 'bg-yellow-500 text-slate-950';
      default:
        return 'bg-primary text-primary-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="rounded-xl border bg-gradient-to-r from-slate-50 via-teal-50 to-slate-50 p-5 dark:from-slate-900 dark:via-teal-950 dark:to-slate-900">
        <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Selamat datang di Reagent Management System</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Sistem pemantauan stock reagen Laboratorium Patologi Klinik. 
          Periode aktif: <span className="font-semibold text-foreground">{data?.label || `${month}/${year}`}</span>.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {KPIS.map((k) => {
          const val = k.isTotal ? data?.total_reagen : data?.counts?.[k.key];
          const Icon = k.Icon;
          return (
            <Card key={k.key} className="relative overflow-hidden p-4" data-testid={`kpi-${k.key}`}>
              <div className={`absolute inset-y-0 left-0 w-1 ${k.accent}`} />
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">{k.label}</span>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="num mt-2 text-3xl font-bold">
                {loading ? <Skeleton className="h-8 w-12" /> : (val ?? 0)}
              </div>
            </Card>
          );
        })}
      </div>

      <WhatsAppCard year={year} month={month} />

      {/* Dynamic Tabbed Table */}
      <Card className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <viewInfo.Icon className={`h-4 w-4 ${viewInfo.iconCls}`} /> {viewInfo.label} ({sortedRows.length})
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Filter Toggle Status */}
            <div className="inline-flex flex-wrap rounded-md border p-0.5 gap-0.5" data-testid="dashboard-status-toggle">
              {Object.entries(STATUS_VIEWS).map(([key, info]) => (
                <button
                  key={key}
                  onClick={() => setView(key)}
                  data-testid={`dashboard-toggle-${key}`}
                  data-active={view === key}
                  className={`rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors ${getTabClass(key, view === key)}`}
                >
                  {info.label.replace('Reagen ', '')} ({getTabCount(key)})
                </button>
              ))}
            </div>

            {/* Dropdown Sort By */}
            <div className="flex items-center gap-1 bg-muted/30 border rounded-md px-2 py-0.5">
              <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="h-7 border-0 bg-transparent text-xs font-medium focus:outline-none cursor-pointer"
              >
                <option value="name_asc">Nama (A - Z)</option>
                <option value="stock_asc">Stok Tersedikit</option>
                <option value="stock_desc">Stok Terbanyak</option>
                <option value="ed_asc">Expired Terdekat</option>
              </select>
            </div>

            <Link to={viewInfo.type === 'ed' ? "/database-stok" : "/pemantauan"} className="flex items-center gap-1 text-xs text-primary hover:underline ml-1" data-testid="link-lihat-semua">
              Lihat semua <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="p-4"><Skeleton className="h-24 w-full" /></div>
        ) : sortedRows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Tidak ada {viewInfo.label.toLowerCase()} pada periode ini.</div>
        ) : (
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 420px)' }}>
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="text-xs uppercase tracking-wide text-muted-foreground border-b">
                  <th className="px-3 py-2.5 text-left w-56 sm:w-64">Nama Reagen</th>
                  <th className="px-3 py-2.5 text-right w-28">Sisa Stock</th>
                  
                  {viewInfo.type === 'stock' ? (
                    <>
                      <th className="px-3 py-2.5 text-right w-32">Pemakaian ({prevMonthName})</th>
                      <th className="px-3 py-2.5 text-right w-32">Pemakaian ({currentMonthName})</th>
                      <th className="px-3 py-2.5 text-right w-24">Buffer</th>
                      <th className="px-3 py-2.5 text-center w-32">Status Stok</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-2.5 text-left w-44">Expired Terdekat (FEFO)</th>
                      <th className="px-3 py-2.5 text-center w-28">Sisa Hari</th>
                      <th className="px-3 py-2.5 text-center w-32">Status ED</th>
                    </>
                  )}
                  
                  <th className="px-3 py-2.5 text-left w-28">PRF</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => {
                  const displayStock = (viewInfo.type === 'ed' && r.sisa_test_fefo !== undefined && r.sisa_test_fefo !== null)
                    ? r.sisa_test_fefo
                    : r.sisa_stock;

                  return (
                    <tr key={r.reagen_id} className="border-t hover:bg-muted/40" data-testid={`dashboard-row-${r.reagen_id}`}>
                      <td className="px-3 py-2 font-medium">{r.nama_reagen}</td>
                      <td className="num px-3 py-2 text-right font-semibold text-primary">
                        {fmtNum(displayStock)}
                      </td>

                      {viewInfo.type === 'stock' ? (
                        <>
                          <td className="num px-3 py-2 text-right text-slate-500">{fmtNum(r.pemakaian_bulan_lalu ?? 0)}</td>
                          <td className="num px-3 py-2 text-right text-slate-700 font-medium">{fmtNum(r.pemakaian_bulan_ini ?? 0)}</td>
                          <td className="num px-3 py-2 text-right text-muted-foreground">{fmtNum(r.buffer_stock)}</td>
                          <td className="px-3 py-2 text-center"><StatusBadge status={r.status} /></td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-left font-medium">
                            {r.expired_date ? fmtDate(r.expired_date) : <span className="text-muted-foreground italic">Tanpa Expired Date</span>}
                          </td>
                          <td className="px-3 py-2 text-center num font-medium">
                            {r.days_left !== null && r.days_left !== undefined ? `${r.days_left} hari` : '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.status_ed === 'expired' && <Badge className="bg-red-600 text-white">Expired</Badge>}
                            {r.status_ed === 'ed_under_3' && <Badge className="bg-orange-500 text-white">&lt; 3 Bulan</Badge>}
                            {r.status_ed === 'ed_3_6' && <Badge className="bg-yellow-500 text-slate-950">3 - 6 Bulan</Badge>}
                          </td>
                        </>
                      )}

                      <td className="px-3 py-2 text-left"><PrfIndicator prf={r.prf} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Quick links */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Akses Cepat</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {QUICK.map((q) => {
            const Icon = q.Icon;
            return (
              <Link key={q.to} to={q.to} data-testid={`quicklink-${q.to.replace('/', '') || 'home'}`}>
                <Card className="group flex items-center gap-3 p-4 transition-colors hover:border-primary hover:bg-accent/40">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent text-accent-foreground">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{q.label}</div>
                    <div className="text-xs text-muted-foreground">{q.desc}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
